import isEqual from 'lodash/isEqual';
import { PayoutIntegrationStatus } from 'anchor-scout-common/types';
import { handlePromise } from '~/infra/utils';
import { InvoiceIntegrationStatus } from '~/models';
import { formatDate } from '~/models/time';
import { selectors } from '~/store/state-selectors';
export async function getAndComparePayoutSummary(api, getState) {
    const [asyncMgmtPayouts, asyncScoutPayouts] = await Promise.all([
        handlePromise(api.payout.getAll()),
        handlePromise(api.payout.getAllFromScout({
            pageSize: 2000,
            pageNumber: 1
        }))
    ]);
    const mgmtPayoutsResponse = asyncMgmtPayouts.data;
    const scoutPayoutsResponse = asyncScoutPayouts.data;
    if (mgmtPayoutsResponse != null && scoutPayoutsResponse != null) {
        const mgmtPayouts = mgmtPayoutsResponse.payouts;
        const scoutPayouts = scoutPayoutsResponse.payouts;
        const businessId = selectors.user.businessId(getState());
        const allDifferences = [];
        const missingInScout = [];
        mgmtPayouts.forEach(async (mgmtPayout)=>{
            const scoutPayout = scoutPayouts.find((p)=>p.id() === mgmtPayout.id());
            if (scoutPayout != null) {
                const mgmtNormalized = normalizePayout(mgmtPayout);
                const scoutNormalized = normalizePayout(scoutPayout);
                const differences = findPayoutDifferences(mgmtNormalized, scoutNormalized);
                if (differences.length > 0) {
                    allDifferences.push({
                        payoutId: mgmtPayout.id(),
                        differences
                    });
                }
            } else {
                missingInScout.push(mgmtPayout.id());
            }
        });
        if (allDifferences.length > 0 || missingInScout.length > 0) {
            const slackMessage = formatPayoutDifferencesMessage(businessId !== null && businessId !== void 0 ? businessId : 'unknown', allDifferences, missingInScout);
            try {
                await api.slack.sendPayoutValidation(slackMessage);
            } catch (error) {}
        }
    }
}
function findPayoutDifferences(mgmt, scout) {
    let path = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : '', depth = arguments.length > 3 && arguments[3] !== void 0 ? arguments[3] : 0;
    const differences = [];
    if (mgmt === scout) return differences;
    if (mgmt == null || scout == null) {
        if (mgmt !== scout) {
            differences.push({
                path: path || 'root',
                mgmt,
                scout
            });
        }
        return differences;
    }
    // Stop recursing too deep to avoid showing internal object structures
    // Instead, just report the objects differ
    if (depth > 5) {
        if (!isEqual(mgmt, scout)) {
            differences.push({
                path: path || 'value',
                mgmt,
                scout
            });
        }
        return differences;
    }
    // Don't recurse into value objects (like Decimal, Money types)
    // They have internal properties like 'd', 'e', 's' that aren't meaningful
    if (typeof mgmt === 'object' && typeof scout === 'object' && !Array.isArray(mgmt) && !Array.isArray(scout)) {
        const mgmtKeys = Object.keys(mgmt);
        const scoutKeys = Object.keys(scout);
        // If object has single-letter keys or looks like a value object, don't recurse
        const hasWeirdKeys = mgmtKeys.some((k)=>k.length === 1 && /[des]/.test(k)) || scoutKeys.some((k)=>k.length === 1 && /[des]/.test(k));
        if (hasWeirdKeys) {
            if (!isEqual(mgmt, scout)) {
                differences.push({
                    path: path || 'value',
                    mgmt,
                    scout
                });
            }
            return differences;
        }
    }
    if (Array.isArray(mgmt) && Array.isArray(scout)) {
        if (mgmt.length > 0 && mgmt[0].id != null) {
            const mgmtById = new Map(mgmt.map((item)=>[
                    item.id,
                    item
                ]));
            const scoutById = new Map(scout.map((item)=>[
                    item.id,
                    item
                ]));
            mgmt.forEach((mgmtItem)=>{
                const scoutItem = scoutById.get(mgmtItem.id);
                if (scoutItem) {
                    const itemDiffs = findPayoutDifferences(mgmtItem, scoutItem, "".concat(path, "[id=").concat(mgmtItem.id, "]"), depth + 1);
                    differences.push(...itemDiffs);
                } else {
                    differences.push({
                        path: "".concat(path, "[id=").concat(mgmtItem.id, "]"),
                        mgmt: 'exists',
                        scout: 'missing'
                    });
                }
            });
            scout.forEach((scoutItem)=>{
                if (!mgmtById.has(scoutItem.id)) {
                    differences.push({
                        path: "".concat(path, "[id=").concat(scoutItem.id, "]"),
                        mgmt: 'missing',
                        scout: 'exists'
                    });
                }
            });
        } else {
            if (mgmt.length !== scout.length) {
                differences.push({
                    path: "".concat(path, ".length"),
                    mgmt: mgmt.length,
                    scout: scout.length
                });
            }
            const minLength = Math.min(mgmt.length, scout.length);
            for(let i = 0; i < minLength; i++){
                const itemDiffs = findPayoutDifferences(mgmt[i], scout[i], "".concat(path, "[").concat(i, "]"), depth + 1);
                differences.push(...itemDiffs);
            }
        }
        return differences;
    }
    if (typeof mgmt === 'object' && typeof scout === 'object') {
        const allKeys = new Set([
            ...Object.keys(mgmt),
            ...Object.keys(scout)
        ]);
        allKeys.forEach((key)=>{
            const newPath = path ? "".concat(path, ".").concat(key) : key;
            const mgmtValue = mgmt[key];
            const scoutValue = scout[key];
            if (!isEqual(mgmtValue, scoutValue)) {
                if (typeof mgmtValue === 'object' && mgmtValue != null || typeof scoutValue === 'object' && scoutValue != null) {
                    const nestedDiffs = findPayoutDifferences(mgmtValue, scoutValue, newPath, depth + 1);
                    differences.push(...nestedDiffs);
                } else {
                    differences.push({
                        path: newPath,
                        mgmt: mgmtValue,
                        scout: scoutValue
                    });
                }
            }
        });
        return differences;
    }
    differences.push({
        path: path || 'value',
        mgmt,
        scout
    });
    return differences;
}
function formatPayoutDifferencesMessage(businessId, allDifferences, missingInScout) {
    const lines = [];
    lines.push("PAYOUT VALIDATION DIFFERENCES DETECTED");
    lines.push("Business: ".concat(businessId));
    lines.push("Payouts with mismatches: ".concat(allDifferences.length));
    if (missingInScout.length > 0) {
        lines.push("Payouts missing in Scout: ".concat(missingInScout.join(', ')));
    }
    lines.push('');
    allDifferences.forEach((payoutDiff)=>{
        lines.push("Payout: ".concat(payoutDiff.payoutId, " (").concat(payoutDiff.differences.length, " mismatches)"));
        payoutDiff.differences.forEach((diff)=>{
            const mgmtStr = formatValue(diff.mgmt);
            const scoutStr = formatValue(diff.scout);
            lines.push("  • ".concat(diff.path, ": MGMT=").concat(mgmtStr, " | SCOUT=").concat(scoutStr));
        });
        lines.push('');
    });
    return lines.join('\n');
}
function formatValue(value) {
    if (value == null) return 'null';
    if (typeof value === 'string') return '"'.concat(value, '"');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}
function normalizeIntegration(integration) {
    if (!integration) return null;
    const result = integration.accept({
        whenSynced: (data)=>({
                status: PayoutIntegrationStatus.Synced,
                date: data.date
            }),
        whenSyncFailed: (failure)=>({
                status: PayoutIntegrationStatus.SyncFailed,
                failureCode: failure.failureCode(),
                failureParam: failure.failureParam(),
                date: failure.date().getTime()
            }),
        whenPendingSync: ()=>({
                status: PayoutIntegrationStatus.PendingSync
            }),
        whenSyncInProgress: ()=>({
                status: PayoutIntegrationStatus.SyncInProgress
            }),
        whenDeleted: (data)=>({
                status: PayoutIntegrationStatus.Deleted,
                date: data.date
            })
    });
    return (result === null || result === void 0 ? void 0 : result.status) === PayoutIntegrationStatus.PendingSync ? null : result;
}
function normalizePayout(payout) {
    const payoutFailure = payout.payoutFailure();
    const qboIntegration = payout.qboIntegration();
    const xeroIntegration = payout.xeroIntegration();
    return {
        id: payout.id(),
        createdAt: payout.createdAt(),
        amount: payout.amount().amount(),
        status: payout.status(),
        formattedNumber: payout.formattedNumber(),
        isPaid: payout.isPaid(),
        payoutFailure: payoutFailure ? {
            code: payoutFailure.code,
            message: payoutFailure.message
        } : null,
        qboIntegration: normalizeIntegration(qboIntegration),
        xeroIntegration: normalizeIntegration(xeroIntegration),
        invoices: payout.invoices().map((invoice)=>({
                id: invoice.id,
                type: invoice.type,
                relationshipId: invoice.relationshipId,
                number: invoice.number,
                formattedNumber: invoice.formattedNumber,
                customer: invoice.customer,
                fee: invoice.fee.amount(),
                processingFee: invoice.processingFee.amount(),
                customerProcessingFee: invoice.customerProcessingFee.amount(),
                disputeFee: invoice.disputeFee.amount(),
                serviceFeeRefund: invoice.serviceFeeRefund.amount(),
                totalFees: invoice.totalFees.amount(),
                totalAmount: invoice.totalAmount.amount(),
                depositAmount: invoice.depositAmount.amount(),
                createdOn: formatDate(invoice.createdOn.getTime()),
                integrations: (()=>{
                    if (!invoice.integrations) return null;
                    const filtered = invoice.integrations.map((integration)=>{
                        const result = integration.integration.accept({
                            whenInvoiceRecorded: (data)=>({
                                    status: InvoiceIntegrationStatus.InvoiceRecorded,
                                    date: data.date
                                }),
                            whenVoidedRecorded: (data)=>({
                                    status: InvoiceIntegrationStatus.VoidedRecorded,
                                    date: data.date
                                }),
                            whenInvoiceSyncFailed: (data)=>({
                                    status: InvoiceIntegrationStatus.InvoiceSyncFailed,
                                    failureCode: data.failure.failureCode(),
                                    failureParam: data.failure.failureParam(),
                                    date: data.failure.date().getTime()
                                }),
                            whenPaymentSyncFailed: (data)=>({
                                    status: InvoiceIntegrationStatus.PaymentSyncFailed,
                                    failureCode: data.failure.failureCode(),
                                    failureParam: data.failure.failureParam(),
                                    date: data.failure.date().getTime()
                                }),
                            whenPendingSync: ()=>({
                                    status: InvoiceIntegrationStatus.PendingSync
                                }),
                            whenPaymentRecorded: (data)=>({
                                    status: InvoiceIntegrationStatus.PaymentRecorded,
                                    date: data.date
                                }),
                            whenRefundRecorded: (data)=>({
                                    status: InvoiceIntegrationStatus.RefundRecorded,
                                    date: data.date,
                                    numRefunds: data.numRefunds
                                }),
                            whenRefundSyncFailed: (data)=>({
                                    status: 'RefundSyncFailed',
                                    failureCode: data.failure.failureCode(),
                                    failureParam: data.failure.failureParam(),
                                    date: data.failure.date().getTime()
                                })
                        });
                        return (result === null || result === void 0 ? void 0 : result.status) === InvoiceIntegrationStatus.PendingSync ? null : {
                            type: integration.type,
                            integration: result
                        };
                    }).filter(Boolean);
                    return filtered.length > 0 ? filtered : null;
                })()
            }))
    };
}
